import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, setButtonEnabled, TYPO } from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { modulePresentationIconKey } from "./modulePresentationIcon.js";
import { ALL_MODULE_MASTERY_DEFINITIONS, type ModuleMasteryBranch } from "../data/moduleMasteryDefinitions.js";
import type { ModuleMasteryRowData, ModuleMasteryTabData } from "./moduleMasteryTabData.js";
import { clampWrappedLines } from "./clampLines.js";
import {
  MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP,
  MODULE_MASTERY_DETAIL_TOP,
  MODULE_MASTERY_PROGRESS_BAR_HEIGHT,
  MODULE_MASTERY_PROGRESS_BAR_WIDTH,
  MODULE_MASTERY_PROGRESS_CENTER_Y,
  MODULE_MASTERY_PROGRESS_PERCENT_GAP,
  MODULE_MASTERY_ROW_HEIGHT,
  computeModuleMasteryRowHeight,
  moduleMasteryBranchOptionIconAlpha,
  moduleMasterySelectedDetailIconAlpha,
  moduleMasterySelectedDetailIconY,
  shouldShowModuleMasteryExpandButton,
} from "./moduleMasteryLayout.js";
import { TipCarouselView } from "./TipCarouselView.js";

export interface ModuleMasteryTabViewOptions {
  /** 분기 선택 — 호출자가 컨트롤러 상태 갱신 + persist + refresh. */
  readonly onSelectBranch: (moduleId: string, branch: ModuleMasteryBranch) => void;
  /** 카드 좌측 모듈 아이콘 클릭 — 도감/모듈 정보 팝업 표시. */
  readonly onOpenModuleInfo: (moduleId: string) => void;
}

const PAD = 12;
const BRANCH_BTN_W = 90;
const BRANCH_BTN_H = 24;
const EXPAND_BTN_W = 64;
const EXPAND_BTN_H = 22;
const EXPAND_BTN_Y = 92;
const BRANCH_EXPAND_BTN_Y = 174;
const TITLE_H = 64;
const TIP_CAROUSEL_H = 40;
const HIDDEN_BRANCH_BUTTON_SCALE = 0.001;
const ICON_BOX = 34;
const ICON_GAP = 8;
const BRANCH_CHOICE_FRAME_Y = 70;
const BRANCH_CHOICE_FRAME_H = 58;
const BRANCH_CHOICE_PAD_X = 12;
const BRANCH_BUTTONS_Y = 82;
const BRANCH_OPTIONS_Y = 112;
const BRANCH_OPTION_ICON_SIZE = 13;
const BRANCH_OPTION_ICON_GAP = 17;
const SELECTED_DETAIL_ICON_SIZE = 12;
const SELECTED_DETAIL_ICON_GAP = 13;
const SELECTED_DETAIL_ICON_COUNT = 3;
const BRANCH_NEXT_GOAL_Y = 156;
const BRANCH_EFFECTS_Y = 176;
const BRANCH_READY_BUTTON_BG_ALPHA = 0.88;

interface RowObjects {
  readonly moduleId: string;
  readonly hasBranch: boolean;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly iconBg: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  readonly name: Phaser.GameObjects.Text;
  readonly mastery: Phaser.GameObjects.Text;
  readonly progressBg: Phaser.GameObjects.Rectangle;
  readonly progressFill: Phaser.GameObjects.Rectangle;
  readonly progressPercent: Phaser.GameObjects.Text;
  readonly effects: Phaser.GameObjects.Text;
  readonly branchChoiceFrame: Phaser.GameObjects.Rectangle;
  readonly branchLabel: Phaser.GameObjects.Text;
  readonly branchOptions: Phaser.GameObjects.Text;
  readonly branchOptionIcons: readonly Phaser.GameObjects.Image[];
  readonly selectedDetailIcons: readonly Phaser.GameObjects.Image[];
  readonly nextGoal: Phaser.GameObjects.Text;
  readonly detail: Phaser.GameObjects.Text;
  readonly expandButton: NineSliceButton;
  readonly branchA: NineSliceButton;
  readonly branchB: NineSliceButton;
}

function detailIconTint(iconKey: string): number {
  return iconKey === "icon_mastery_effect_boost" || iconKey === "icon_mastery_branch_focus"
    ? PALETTE_NUM.accentGold
    : PALETTE_NUM.gridPathGlow;
}

/**
 * 모듈 숙련도 탭 — 모듈별 카드(이름/숙련 레벨/XP 진행/효과/분기/다음 목표). 스탯 탭과 동형
 * (ScrollableList 스크롤 + 행 1회 생성 후 update는 텍스트/버튼 상태만 갱신, Humble View). 데이터 조립은
 * buildModuleMasteryData(순수)가 담당하고 뷰는 setText/setEnabled만 한다.
 *
 * 비분기 노드는 XP 임계 도달 시 자동 해금(표시만), 분기 노드는 selectedBranch 일치 시 효과 적용 —
 * 본 탭의 A/B 버튼은 레벨 3 임계 도달 후 미선택 상태에서만 노출한다.
 */
export class ModuleMasteryTabView implements TabViewContract<ModuleMasteryTabData> {
  private readonly title: Phaser.GameObjects.Text;
  private readonly tipView: TipCarouselView;
  private readonly scrollList: ScrollableList;
  private readonly rows: RowObjects[] = [];
  private readonly expandedModuleIds = new Set<string>();
  private rowData = new Map<string, ModuleMasteryRowData>();
  private readonly rect: { x: number; y: number; width: number };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly panelBottom: number,
    opts: ModuleMasteryTabViewOptions,
  ) {
    this.rect = { x, y, width };
    const dir = getTextDirection();

    this.title = scene.add
      .text(x + width / 2, y, t("mastery.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5, 0)
      .setDepth(3);

    this.tipView = new TipCarouselView(scene, x, y + 20, width, TIP_CAROUSEL_H, "moduleMastery", () => 0, {
      showArrows: false,
    });
    this.tipView.setDepth(3);
    scene.add.existing(this.tipView);

    const scroll = this.scrollGeom();
    this.scrollList = new ScrollableList(scene, scroll.x, scroll.y, {
      width: scroll.width,
      height: scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);

    ALL_MODULE_MASTERY_DEFINITIONS.forEach((def) => {
      const hasBranch = def.nodes.some((n) => n.branch !== undefined);
      const frame = scene.add
        .rectangle(0, 0, width - 8, MODULE_MASTERY_ROW_HEIGHT - 8, PALETTE_NUM.cellBg, 0.88)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(1);
      const iconBg = scene.add
        .rectangle(0, 0, ICON_BOX, ICON_BOX, PALETTE_NUM.buttonBg, 0.62)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.45)
        .setDepth(2)
        .setInteractive({ useHandCursor: true });
      iconBg.on("pointerup", () => {
        if (this.scrollList.consumedDragGesture) return;
        opts.onOpenModuleInfo(def.moduleId);
      });
      const icon = scene.add
        .image(0, 0, modulePresentationIconKey(def.moduleId))
        .setDisplaySize(22, 22)
        .setTint(PALETTE_NUM.gridPathGlow)
        .setDepth(3);
      const name = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.label, color: PALETTE.textPrimary })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const mastery = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary })
        .setOrigin(1, 0.5)
        .setDepth(3);
      const progressBg = scene.add
        .rectangle(0, 0, MODULE_MASTERY_PROGRESS_BAR_WIDTH, MODULE_MASTERY_PROGRESS_BAR_HEIGHT, PALETTE_NUM.buttonBg, 0.72)
        .setOrigin(0, 0.5)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.35)
        .setDepth(3);
      const progressFill = scene.add
        .rectangle(0, 0, 1, MODULE_MASTERY_PROGRESS_BAR_HEIGHT, PALETTE_NUM.gridPathGlow, 0.9)
        .setOrigin(0, 0.5)
        .setDepth(4);
      const progressPercent = scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
        })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const effects = scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
        })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const branchChoiceFrame = scene.add
        .rectangle(0, 0, 1, BRANCH_CHOICE_FRAME_H, PALETTE_NUM.buttonBg, 0.54)
        .setStrokeStyle(1, PALETTE_NUM.accentGold, 0.72)
        .setDepth(2)
        .setVisible(false);
      const branchLabel = scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textDim,
        })
        .setOrigin(0, 0.5)
        .setDepth(3)
        .setVisible(hasBranch);
      const branchOptions = scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
          lineSpacing: 2,
        })
        .setOrigin(0, 0)
        .setDepth(3)
        .setVisible(false);
      const branchOptionIcons = [
        scene.add
          .image(0, 0, "icon_mastery_branch_spread")
          .setDisplaySize(BRANCH_OPTION_ICON_SIZE, BRANCH_OPTION_ICON_SIZE),
        scene.add
          .image(0, 0, "icon_mastery_branch_focus")
          .setDisplaySize(BRANCH_OPTION_ICON_SIZE, BRANCH_OPTION_ICON_SIZE),
      ] as const;
      branchOptionIcons.forEach((icon) => icon.setDepth(3).setVisible(false));
      const selectedDetailIcons = Array.from({ length: SELECTED_DETAIL_ICON_COUNT }, () =>
        scene.add
          .image(0, 0, "icon_mastery_effect_stable")
          .setDisplaySize(SELECTED_DETAIL_ICON_SIZE, SELECTED_DETAIL_ICON_SIZE)
          .setDepth(3)
          .setVisible(false)
          .setAlpha(0),
      );
      const nextGoal = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.positiveSoft })
        .setOrigin(0, 0.5)
        .setDepth(3);
      const detail = scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
          lineSpacing: 2,
        })
        .setOrigin(0, 0)
        .setDepth(3)
        .setVisible(false);
      const expandButton = new NineSliceButton(scene, 0, 0, {
        style: PRIMARY_BUTTON_STYLE,
        label: t("mastery.expand"),
        widthOverride: EXPAND_BTN_W,
        heightOverride: EXPAND_BTN_H,
      });
      expandButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.toggleExpanded(def.moduleId));
      const branchA = this.makeBranchButton(
        scene,
        "A",
        "icon_mastery_branch_spread",
        () => opts.onSelectBranch(def.moduleId, "A"),
        hasBranch,
      );
      const branchB = this.makeBranchButton(
        scene,
        "B",
        "icon_mastery_branch_focus",
        () => opts.onSelectBranch(def.moduleId, "B"),
        hasBranch,
      );

      this.scrollList.addContent(frame, 0, 0);
      this.scrollList.addContent(iconBg, 0, 0);
      this.scrollList.addContent(icon, 0, 0);
      this.scrollList.addContent(name, 0, 0);
      this.scrollList.addContent(mastery, 0, 0);
      this.scrollList.addContent(progressBg, 0, 0);
      this.scrollList.addContent(progressFill, 0, 0);
      this.scrollList.addContent(progressPercent, 0, 0);
      this.scrollList.addContent(effects, 0, 0);
      this.scrollList.addContent(branchChoiceFrame, 0, 0);
      this.scrollList.addContent(branchLabel, 0, 0);
      this.scrollList.addContent(branchOptions, 0, 0);
      branchOptionIcons.forEach((icon) => this.scrollList.addContent(icon, 0, 0));
      selectedDetailIcons.forEach((icon) => this.scrollList.addContent(icon, 0, 0));
      this.scrollList.addContent(nextGoal, 0, 0);
      this.scrollList.addContent(detail, 0, 0);
      this.scrollList.addContent(expandButton, 0, 0);
      this.scrollList.addContent(branchA, 0, 0);
      this.scrollList.addContent(branchB, 0, 0);
      this.rows.push({
        moduleId: def.moduleId,
        hasBranch,
        frame,
        iconBg,
        icon,
        name,
        mastery,
        progressBg,
        progressFill,
        progressPercent,
        effects,
        branchChoiceFrame,
        branchLabel,
        branchOptions,
        branchOptionIcons,
        selectedDetailIcons,
        nextGoal,
        detail,
        expandButton,
        branchA,
        branchB,
      });
    });

    this.scrollList.setContentHeight(ALL_MODULE_MASTERY_DEFINITIONS.length * MODULE_MASTERY_ROW_HEIGHT);
    this.layoutRows(dir);
  }

  private makeBranchButton(
    scene: Phaser.Scene,
    label: string,
    iconKey: string,
    onClick: () => void,
    visible: boolean,
  ): NineSliceButton {
    const btn = new NineSliceButton(scene, 0, 0, {
      style: PRIMARY_BUTTON_STYLE,
      label,
      widthOverride: BRANCH_BTN_W,
      heightOverride: BRANCH_BTN_H,
      iconKey,
      iconSize: 13,
    });
    btn.on(NINE_SLICE_BUTTON_EVENT.Click, onClick);
    btn.setVisible(visible);
    return btn;
  }

  private applyBranchButtonState(btn: NineSliceButton, visible: boolean, alpha: number, bgTint?: number): void {
    setButtonEnabled(btn, visible);
    if (visible && bgTint !== undefined) {
      btn.setBgTint(bgTint);
      btn.setBgAlpha(BRANCH_READY_BUTTON_BG_ALPHA);
    }
    btn.setAlpha(visible ? alpha : 0);
    btn.setScale(visible ? 1 : HIDDEN_BRANCH_BUTTON_SCALE);
  }

  private toggleExpanded(moduleId: string): void {
    if (!this.hasEffectDetails(moduleId)) {
      this.expandedModuleIds.delete(moduleId);
      this.layoutRows(getTextDirection());
      return;
    }
    if (this.expandedModuleIds.has(moduleId)) this.expandedModuleIds.delete(moduleId);
    else this.expandedModuleIds.add(moduleId);
    this.layoutRows(getTextDirection());
  }

  private hasEffectDetails(moduleId: string): boolean {
    return (this.rowData.get(moduleId)?.effectDetailLines.length ?? 0) > 0;
  }

  private hasBranchOptions(moduleId: string): boolean {
    const data = this.rowData.get(moduleId);
    return data?.canChooseBranch === true && data.branchOptions.length > 0;
  }

  private hasSelectedBranch(moduleId: string): boolean {
    return this.rowData.get(moduleId)?.selectedBranch !== undefined;
  }

  private rowHeight(row: RowObjects, detailContentHeight: number): number {
    const data = this.rowData.get(row.moduleId);
    return computeModuleMasteryRowHeight({
      expanded: this.expandedModuleIds.has(row.moduleId),
      hasEffectDetails: shouldShowModuleMasteryExpandButton((data?.effectDetailLines.length ?? 0) > 0),
      detailContentHeight,
      hasBranchOptions: this.hasBranchOptions(row.moduleId),
      hasSelectedBranch: this.hasSelectedBranch(row.moduleId),
    });
  }

  private setClampedText(obj: Phaser.GameObjects.Text, value: string, maxLines: number): void {
    obj.setText(clampWrappedLines(obj.getWrappedText(value), maxLines));
  }

  /** 스크롤 영역 — 제목 아래 ~ 패널 하단. */
  private scrollGeom(): { x: number; y: number; width: number; height: number } {
    const top = this.rect.y + TITLE_H;
    return { x: this.rect.x, y: top, width: this.rect.width, height: Math.max(40, this.panelBottom - top) };
  }

  /** 행 내부 로컬 좌표 배치 (RTL이면 좌/우 반전). */
  private layoutRows(dir: TextDirection): void {
    const W = this.rect.width;
    const rtl = dir === "rtl";
    const textLeftX = rtl ? W - PAD - ICON_BOX - ICON_GAP : PAD + ICON_BOX + ICON_GAP;
    const iconX = rtl ? W - PAD - ICON_BOX / 2 : PAD + ICON_BOX / 2;
    const rightX = rtl ? PAD : W - PAD;
    const textWrapWidth = W - PAD * 2 - ICON_BOX - ICON_GAP;
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    let top = 0;
    this.rows.forEach((row) => {
      const data = this.rowData.get(row.moduleId);
      const branchReady = data?.canChooseBranch === true;
      const hasEffectDetails = shouldShowModuleMasteryExpandButton((data?.effectDetailLines.length ?? 0) > 0);
      const expanded = hasEffectDetails && this.expandedModuleIds.has(row.moduleId);
      if (!hasEffectDetails) this.expandedModuleIds.delete(row.moduleId);
      row.name.setOrigin(rtl ? 1 : 0, 0.5);
      row.mastery.setOrigin(rtl ? 0 : 1, 0.5);
      row.effects.setOrigin(rtl ? 1 : 0, 0.5);
      row.branchLabel.setOrigin(rtl ? 1 : 0, 0.5);
      row.branchOptions.setOrigin(rtl ? 1 : 0, 0);
      row.nextGoal.setOrigin(rtl ? 1 : 0, 0.5);
      row.progressPercent.setOrigin(rtl ? 1 : 0, 0.5);
      row.effects.setWordWrapWidth(Math.max(90, textWrapWidth - EXPAND_BTN_W - 10));
      const branchChoiceFrameWidth = Math.max(222, Math.min(textWrapWidth, BRANCH_BTN_W * 2 + BRANCH_CHOICE_PAD_X * 2 + 18));
      const branchChoiceLeftX = rtl ? textLeftX - branchChoiceFrameWidth : textLeftX;
      const branchChoiceContentX = rtl
        ? branchChoiceLeftX + branchChoiceFrameWidth - BRANCH_CHOICE_PAD_X
        : branchChoiceLeftX + BRANCH_CHOICE_PAD_X;
      row.branchLabel.setWordWrapWidth(Math.max(90, branchChoiceFrameWidth - BRANCH_CHOICE_PAD_X * 2));
      row.branchOptions.setWordWrapWidth(Math.max(90, textWrapWidth - BRANCH_OPTION_ICON_GAP));
      row.nextGoal.setWordWrapWidth(Math.max(90, textWrapWidth - EXPAND_BTN_W - 10));
      row.detail.setOrigin(rtl ? 1 : 0, 0).setWordWrapWidth(textWrapWidth);
      row.detail.setVisible(expanded);
      row.branchLabel.setVisible(row.hasBranch && (data?.branchStatusLabel ?? "").length > 0);
      row.branchChoiceFrame
        .setVisible(branchReady)
        .setAlpha(branchReady ? 1 : 0)
        .setFillStyle(PALETTE_NUM.buttonBg, 0.54)
        .setStrokeStyle(1, PALETTE_NUM.accentGold, 0.72);
      row.branchLabel.setColor(branchReady ? PALETTE.accentGold : PALETTE.textDim);
      row.branchOptions.setColor(branchReady ? PALETTE.textPrimary : PALETTE.textSecondary);
      row.nextGoal.setColor(branchReady ? PALETTE.accentGold : PALETTE.positiveSoft);
      const showBranchOptions = this.hasBranchOptions(row.moduleId);
      const progressVisible = data?.progress.progressVisible === true;
      const progressWidth = Math.max(0, Math.round(MODULE_MASTERY_PROGRESS_BAR_WIDTH * (data?.progress.progressRatio ?? 0)));
      const progressFillVisible = progressVisible && progressWidth > 0;
      row.progressBg.setVisible(progressVisible).setAlpha(progressVisible ? 1 : 0);
      row.progressFill
        .setVisible(progressFillVisible)
        .setAlpha(progressFillVisible ? 1 : 0)
        .setSize(Math.max(1, progressWidth), MODULE_MASTERY_PROGRESS_BAR_HEIGHT)
        .setDisplaySize(Math.max(1, progressWidth), MODULE_MASTERY_PROGRESS_BAR_HEIGHT);
      row.progressPercent
        .setVisible(progressVisible)
        .setAlpha(progressVisible ? 1 : 0)
        .setText(data?.progress.progressPercentLabel ?? "");
      row.branchOptions.setVisible(showBranchOptions).setAlpha(showBranchOptions ? 1 : 0);
      row.selectedDetailIcons.forEach((icon, idx) => {
        const detailRow = data?.effectDetailRows[idx];
        const hasIcon = detailRow?.iconKey !== undefined;
        icon
          .setVisible(expanded && hasIcon)
          .setAlpha(moduleMasterySelectedDetailIconAlpha({ expanded, hasIcon }));
        if (!detailRow?.iconKey) return;
        icon.setTexture(detailRow.iconKey);
        icon.setTint(detailIconTint(detailRow.iconKey));
      });
      row.expandButton.setVisible(hasEffectDetails);
      setButtonEnabled(row.expandButton, hasEffectDetails);
      row.expandButton.setLabel(t(expanded ? "mastery.collapse" : "mastery.expand"));
      row.detail.setText(expanded && data ? data.effectDetailLines.join("\n") : "");
      if (data) {
        row.branchOptionIcons.forEach((icon, idx) => {
          const option = data.branchOptions[idx];
          icon.setVisible(showBranchOptions && option !== undefined);
          icon.setAlpha(
            moduleMasteryBranchOptionIconAlpha({
              showBranchOptions,
              optionIndex: idx,
              optionCount: data.branchOptions.length,
              ready: option?.ready ?? false,
            }),
          );
          if (!option) return;
          icon.setTexture(option.iconKey);
          icon.setTint(detailIconTint(option.iconKey));
        });
        this.setClampedText(row.effects, data.effectsLabel, 1);
        this.setClampedText(row.nextGoal, data.nextGoalLabel, 1);
        this.setClampedText(row.branchLabel, data.branchStatusLabel, 1);
        this.setClampedText(
          row.branchOptions,
          showBranchOptions
            ? data.branchOptions.map((o) => `${o.name}: ${o.effectSummary}`).join("\n")
            : "",
          2,
        );
      }
      const h = this.rowHeight(row, expanded ? row.detail.height : 0);
      const cy = top + h / 2;
      row.frame
        .setFillStyle(PALETTE_NUM.cellBg, branchReady ? 0.96 : 0.88)
        .setStrokeStyle(
          branchReady ? 2 : 1,
          branchReady ? PALETTE_NUM.accentGold : PALETTE_NUM.gridPathGlow,
          branchReady ? 0.82 : 0.28,
        )
        .setSize(W - 8, h - 8)
        .setDisplaySize(W - 8, h - 8);
      row.iconBg.setStrokeStyle(
        1,
        branchReady ? PALETTE_NUM.accentGold : PALETTE_NUM.gridPathGlow,
        branchReady ? 0.9 : 0.45,
      );
      row.icon.setTint(branchReady ? PALETTE_NUM.accentGold : PALETTE_NUM.gridPathGlow);
      // 분기 버튼 — 선택 안내 문구 아래 두 번째 줄에 배치한다.
      const bX1 = rtl ? branchChoiceContentX - BRANCH_BTN_W / 2 : branchChoiceContentX + BRANCH_BTN_W / 2;
      const bX2 = rtl ? bX1 - BRANCH_BTN_W - 6 : bX1 + BRANCH_BTN_W + 6;
      const branchChoiceFrameX = branchChoiceLeftX + branchChoiceFrameWidth / 2;
      row.branchChoiceFrame.setSize(branchChoiceFrameWidth, BRANCH_CHOICE_FRAME_H).setDisplaySize(
        branchChoiceFrameWidth,
        BRANCH_CHOICE_FRAME_H,
      );
      const branchOptionIconX = rtl ? textLeftX : textLeftX + BRANCH_OPTION_ICON_SIZE / 2;
      const branchOptionTextX = rtl ? textLeftX - BRANCH_OPTION_ICON_GAP : textLeftX + BRANCH_OPTION_ICON_GAP;
      const progressBarX = rtl ? textLeftX - MODULE_MASTERY_PROGRESS_BAR_WIDTH : textLeftX;
      const progressPercentX = rtl
        ? textLeftX - MODULE_MASTERY_PROGRESS_BAR_WIDTH - MODULE_MASTERY_PROGRESS_PERCENT_GAP
        : textLeftX + MODULE_MASTERY_PROGRESS_BAR_WIDTH + MODULE_MASTERY_PROGRESS_PERCENT_GAP;
      const detailTop = showBranchOptions ? MODULE_MASTERY_BRANCH_OPTIONS_DETAIL_TOP : MODULE_MASTERY_DETAIL_TOP;
      const selectedDetailIconX = rtl ? textLeftX + SELECTED_DETAIL_ICON_GAP : textLeftX - SELECTED_DETAIL_ICON_GAP;
      row.selectedDetailIcons.forEach((icon, lineIndex) => {
        positions.push({
          obj: icon,
          lx: selectedDetailIconX,
          ly: moduleMasterySelectedDetailIconY({
            rowTop: top,
            detailTopOffset: detailTop,
            lineIndex,
          }),
        });
      });
      positions.push(
        { obj: row.frame, lx: W / 2, ly: cy },
        { obj: row.iconBg, lx: iconX, ly: top + 26 },
        { obj: row.icon, lx: iconX, ly: top + 26 },
        { obj: row.name, lx: textLeftX, ly: top + 16 },
        { obj: row.mastery, lx: rightX, ly: top + 16 },
        { obj: row.progressBg, lx: progressBarX, ly: top + MODULE_MASTERY_PROGRESS_CENTER_Y },
        { obj: row.progressFill, lx: progressBarX, ly: top + MODULE_MASTERY_PROGRESS_CENTER_Y },
        { obj: row.progressPercent, lx: progressPercentX, ly: top + MODULE_MASTERY_PROGRESS_CENTER_Y },
        { obj: row.branchChoiceFrame, lx: branchChoiceFrameX, ly: top + BRANCH_CHOICE_FRAME_Y },
        { obj: row.branchLabel, lx: branchChoiceContentX, ly: top + 56 },
        { obj: row.branchA, lx: bX1, ly: top + BRANCH_BUTTONS_Y },
        { obj: row.branchB, lx: bX2, ly: top + BRANCH_BUTTONS_Y },
        { obj: row.branchOptionIcons[0]!, lx: branchOptionIconX, ly: top + BRANCH_OPTIONS_Y + 7 },
        { obj: row.branchOptionIcons[1]!, lx: branchOptionIconX, ly: top + BRANCH_OPTIONS_Y + 25 },
        { obj: row.branchOptions, lx: branchOptionTextX, ly: top + BRANCH_OPTIONS_Y },
        { obj: row.nextGoal, lx: textLeftX, ly: top + (showBranchOptions ? BRANCH_NEXT_GOAL_Y : 76) },
        { obj: row.effects, lx: textLeftX, ly: top + (showBranchOptions ? BRANCH_EFFECTS_Y : 96) },
        {
          obj: row.expandButton,
          lx: rtl ? PAD + EXPAND_BTN_W / 2 : W - PAD - EXPAND_BTN_W / 2,
          ly: top + (showBranchOptions ? BRANCH_EXPAND_BTN_Y : EXPAND_BTN_Y),
        },
        {
          obj: row.detail,
          lx: textLeftX,
          ly: top + detailTop,
        },
      );
      top += h;
    });
    this.scrollList.relayoutContent(positions);
    this.scrollList.setContentHeight(top);
  }

  update(d: ModuleMasteryTabData): void {
    const byId = new Map<string, ModuleMasteryRowData>(d.rows.map((r) => [r.moduleId, r]));
    this.rowData = byId;
    for (const row of this.rows) {
      const data = byId.get(row.moduleId);
      if (!data) continue;
      row.name.setText(data.name);
      row.mastery.setText(data.masteryLabel);
      row.effects.setText(data.effectsLabel);
      row.branchLabel.setText(data.branchStatusLabel);
      row.nextGoal.setText(data.nextGoalLabel);
      if (!row.hasBranch) continue;
      // 버튼 라벨 = 현지화된 분기 이름. 조건 달성 후 미선택 상태에서만 버튼을 보여준다.
      row.branchA.setLabel(data.branchAName);
      row.branchB.setLabel(data.branchBName);
      row.branchA.setIcon(
        data.branchOptions.find((o) => o.branch === "A")?.iconKey ?? "icon_mastery_branch_spread",
      );
      row.branchB.setIcon(
        data.branchOptions.find((o) => o.branch === "B")?.iconKey ?? "icon_mastery_branch_focus",
      );
      row.branchA.setIconTint(PALETTE_NUM.gridPathGlow);
      row.branchB.setIconTint(PALETTE_NUM.accentGold);
      row.branchA.setIconVisible(data.canChooseBranch && data.branchAReady);
      row.branchB.setIconVisible(data.canChooseBranch && data.branchBReady);
      this.applyBranchButtonState(row.branchA, data.canChooseBranch && data.branchAReady, 1, PALETTE_NUM.gridPathGlow);
      this.applyBranchButtonState(row.branchB, data.canChooseBranch && data.branchBReady, 1, PALETTE_NUM.accentGold);
    }
    this.layoutRows(getTextDirection());
  }

  applyLanguage(): void {
    this.title.setText(t("mastery.title"));
    this.tipView.applyLanguage();
  }

  relayout(dir: TextDirection): void {
    this.title.setPosition(this.rect.x + this.rect.width / 2, this.rect.y);
    this.tipView.setPosition(this.rect.x, this.rect.y + 20);
    this.tipView.relayout(dir);
    this.layoutRows(dir);
  }

  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.title.setPosition(rect.x + rect.width / 2, rect.y);
    this.tipView.setPosition(rect.x, rect.y + 20);
    const s = this.scrollGeom();
    this.scrollList.resize(s.x, s.y, s.width, s.height);
    this.layoutRows(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.title.setVisible(visible);
    this.tipView.setVisible(visible);
    this.scrollList.setVisible(visible);
  }
}
